import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardPagination from '../components/DashboardPagination';
import SearchInput from '../components/SearchInput';
import planoService from '../services/planoTrabalhoService';
import prestacaoService, { type PrestacaoPc, type PrestacaoPcSituacao } from '../services/prestacaoContaService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import { UI_THEME } from '../ui/theme';
import { SITUACAO_PC_LABEL, SituacaoPcBadge } from './prestacaoContasRelatorios/PrestacaoPcAprovacaoPanel';

/** Uma linha da lista = uma PC (cada PC tem situação, prazo e responsável próprios). */
type LinhaPc = {
  plano: PlanoTrabalho;
  pc: PrestacaoPc;
  totalPcsDoPlano: number;
  totalExecutado: number;
  ultimaMovimentacao?: string;
};

type FiltroSituacao = 'todas' | PrestacaoPcSituacao;

/** Ordem do ciclo de aprovação da PC, como definido no requisito. */
const SITUACOES: { key: PrestacaoPcSituacao; description: string; dot: string }[] = [
  { key: 'PENDENTE', description: 'PC criada, sem nenhum dado preenchido', dot: 'bg-slate-400' },
  { key: 'RASCUNHO', description: 'Já tem o primeiro dado inserido', dot: 'bg-sky-500' },
  { key: 'EM_ANALISE', description: 'Enviada para aprovação, travada para alteração', dot: 'bg-amber-500' },
  { key: 'EM_CORRECAO', description: 'Correção pedida; instituição pode alterar', dot: 'bg-rose-500' },
  { key: 'APROVADO', description: 'PC aprovada', dot: 'bg-emerald-500' },
  { key: 'APROVADO_COM_RESSALVA', description: 'Aprovada com ressalvas', dot: 'bg-teal-500' },
  { key: 'REPROVADO', description: 'PC reprovada', dot: 'bg-red-600' },
];

/** Situações em que ainda há prazo correndo para alguém agir. */
const SITUACOES_COM_PRAZO_ABERTO: PrestacaoPcSituacao[] = ['PENDENTE', 'RASCUNHO', 'EM_CORRECAO', 'EM_ANALISE'];
const DIAS_ALERTA_PRAZO = 15;

const STATUS_LABEL: Record<string, string> = {
  CONCLUIDO: 'Concluído',
  EM_PRESTACAO: 'Em prestação',
  FECHADO: 'Fechado',
};

const currencyFmt = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarData = (valor?: string | null) => {
  if (!valor) return '—';
  const data = new Date(valor.length === 10 ? `${valor}T00:00:00` : valor);
  return Number.isNaN(data.getTime()) ? valor : data.toLocaleDateString('pt-BR');
};

const PrestacoesPlanosGestaoPage: React.FC = () => {
  const PAGE_SIZE_OPTIONS = [10, 20, 50];
  const navigate = useNavigate();
  const [linhas, setLinhas] = useState<LinhaPc[]>([]);
  const [totalPlanos, setTotalPlanos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState<FiltroSituacao>('todas');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const carregamentoInicialExecutado = useRef(false);

  const isSaas = UI_THEME === 'saas';
  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const mutedSurfaceClass = isSaas
    ? 'border-teal-100 bg-teal-50/60 dark:border-teal-900/60 dark:bg-teal-950/30'
    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-blue-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-blue-700 dark:text-blue-300';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';
  const primaryButtonClass = isSaas
    ? 'bg-teal-600 text-white hover:bg-teal-700 dark:bg-teal-500 dark:hover:bg-teal-400'
    : 'bg-blue-700 text-white hover:bg-blue-800 dark:bg-blue-600 dark:hover:bg-blue-500';
  const secondaryButtonClass = 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800';

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const planos = await planoService.listAll();
      const planosLista = Array.isArray(planos) ? planos : [];
      const resumos = await prestacaoService.getResumos(planosLista.map((plano) => plano.id));
      const resumosPorPlano = new Map(resumos.map((resumo) => [resumo.planoId, resumo]));

      const resultado: LinhaPc[] = [];
      planosLista.forEach((plano) => {
        const resumo = resumosPorPlano.get(plano.id);
        if (!resumo) return;
        resumo.pcs.forEach((pc) => {
          const datas = pc.comprovantes.map((item) => item.dataCriacao).filter(Boolean).sort();
          resultado.push({
            plano,
            pc,
            totalPcsDoPlano: resumo.pcs.length,
            totalExecutado: pc.comprovantes.reduce((soma, item) => soma + Number(item.valor || 0), 0),
            ultimaMovimentacao: datas.at(-1) || plano.dataAtualizacao || plano.aprovadoEm || plano.dataCriacao,
          });
        });
      });

      resultado.sort((a, b) => (a.plano.titulo || '').localeCompare(b.plano.titulo || '', 'pt-BR') || a.pc.numero - b.pc.numero);
      setLinhas(resultado);
      setTotalPlanos(planosLista.length);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Não foi possível carregar a gestão de prestações de contas.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (carregamentoInicialExecutado.current) return;
    carregamentoInicialExecutado.current = true;
    fetchData();
  }, []);

  const filteredRows = useMemo(() => {
    let result = linhas;
    if (activeFilter !== 'todas') {
      result = result.filter((linha) => linha.pc.situacao === activeFilter);
    }
    const termo = busca.trim().toLowerCase();
    if (termo) {
      result = result.filter((linha) => [
        linha.plano.titulo,
        linha.plano.emendaCodigo,
        linha.plano.instituicaoNome,
        linha.plano.numeroProtocolo,
        linha.pc.protocolo,
      ].some((campo) => (campo || '').toLowerCase().includes(termo)));
    }
    return result;
  }, [activeFilter, busca, linhas]);

  const metricas = useMemo(() => {
    const total = (situacao: PrestacaoPcSituacao) => linhas.filter((linha) => linha.pc.situacao === situacao).length;
    return [
      { label: 'PCs monitoradas', value: linhas.length, dot: 'bg-slate-300', hint: `${new Set(linhas.map((l) => l.plano.id)).size} de ${totalPlanos} planos` },
      ...SITUACOES.map((item) => ({ label: SITUACAO_PC_LABEL[item.key], value: total(item.key), dot: item.dot, hint: '' })),
    ];
  }, [linhas, totalPlanos]);

  const filterOptions = [
    { key: 'todas' as FiltroSituacao, title: 'Todas', description: 'Todas as PCs monitoradas' },
    ...SITUACOES.map((item) => ({ key: item.key as FiltroSituacao, title: SITUACAO_PC_LABEL[item.key], description: item.description })),
  ];

  const paginatedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [page, pageSize, filteredRows]);

  useEffect(() => {
    setPage(1);
  }, [activeFilter, busca, filteredRows.length, pageSize]);

  const renderPrazo = (pc: PrestacaoPc) => {
    const prazoAberto = SITUACOES_COM_PRAZO_ABERTO.includes(pc.situacao);
    let alerta: React.ReactNode = null;
    if (prazoAberto && pc.prazoExpirado) {
      alerta = <span className="text-[11px] font-semibold text-red-700 dark:text-red-300">Prazo vencido</span>;
    } else if (prazoAberto && pc.diasRestantes != null && pc.diasRestantes <= DIAS_ALERTA_PRAZO) {
      alerta = (
        <span className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">
          {pc.diasRestantes === 0 ? 'Vence hoje' : `Vence em ${pc.diasRestantes} dia(s)`}
        </span>
      );
    }
    return (
      <div className="space-y-0.5">
        <p className={`font-medium ${bodyTextClass}`}>{formatarData(pc.prazoPrestacaoFinal)}</p>
        {alerta}
      </div>
    );
  };

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-3xl border ${heroClass}`}>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-2">
            <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
              {isSaas ? 'Financeiro' : 'Gestão'}
            </p>
            <div>
              <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
                {isSaas ? 'Prestação dos planos' : 'Prestação de contas dos planos'}
              </h1>
              <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>
                Cada linha é uma PC, com a situação (pendente, rascunho, em análise, correção, aprovada ou reprovada) e o prazo próprios.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={fetchData}
            className={`inline-flex items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold transition ${primaryButtonClass}`}
          >
            Atualizar
          </button>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 dark:divide-slate-700 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4">
            {metricas.map((metrica) => (
              <div key={metrica.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${metrica.dot}`} />
                  {metrica.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{metrica.value}</p>
                {metrica.hint && <p className={`text-[11px] ${labelTextClass}`}>{metrica.hint}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`space-y-3 rounded-3xl border p-3 ${surfaceClass}`}>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {filterOptions.map((item) => {
            const active = activeFilter === item.key;
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => setActiveFilter(item.key)}
                className={`rounded-2xl border px-4 py-3 text-left transition-colors ${
                  active
                    ? `${isSaas ? 'border-teal-200 bg-teal-50 dark:border-teal-800 dark:bg-teal-950/50' : 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/50'} shadow-sm`
                    : `${mutedSurfaceClass} hover:bg-slate-100 dark:hover:bg-slate-800`
                }`}
              >
                <p className={`text-sm font-semibold ${active ? accentTextClass : strongTextClass}`}>{item.title}</p>
                <p className={`mt-1 text-xs leading-5 ${active ? bodyTextClass : labelTextClass}`}>{item.description}</p>
              </button>
            );
          })}
        </div>

        <SearchInput
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por emenda, instituição, plano ou protocolo..."
          className="px-1"
        />
      </div>

      {loading ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass} ${labelTextClass}`}>
          Carregando prestações de contas...
        </div>
      ) : error ? (
        <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-center shadow-sm dark:border-red-900/60 dark:bg-red-950/30">
          <p className="font-semibold text-red-800 dark:text-red-200">Erro ao carregar prestações</p>
          <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
        </div>
      ) : filteredRows.length === 0 ? (
        <div className={`rounded-3xl border p-12 text-center ${surfaceClass}`}>
          <p className={`text-lg font-semibold ${strongTextClass}`}>Nenhuma PC encontrada nesta visão.</p>
          <p className={`mt-2 text-sm ${labelTextClass}`}>
            {linhas.length === 0
              ? 'As PCs são criadas quando Convênios conclui a formalização do termo de fomento.'
              : 'Quando houver PCs aderentes ao filtro escolhido, elas aparecerão aqui.'}
          </p>
        </div>
      ) : (
        <div className={`overflow-hidden rounded-3xl border ${surfaceClass}`}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/80">
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className={`px-5 py-4 text-left font-semibold ${bodyTextClass}`}>Plano</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>PC</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Situação</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Prazo final</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Comprovantes</th>
                <th className={`px-4 py-4 text-left font-semibold ${bodyTextClass}`}>Total executado</th>
                <th className={`px-5 py-4 text-right font-semibold ${bodyTextClass}`}>Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {paginatedRows.map(({ plano, pc, totalPcsDoPlano, totalExecutado, ultimaMovimentacao }) => (
                <tr key={pc.pcId} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/60">
                  <td className="px-5 py-4 align-top">
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className={`font-semibold ${strongTextClass}`}>{plano.titulo}</p>
                        <span className="inline-flex rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
                          {STATUS_LABEL[plano.status || ''] || plano.status}
                        </span>
                      </div>
                      <p className={`text-xs ${labelTextClass}`}>
                        {plano.instituicaoNome || 'Instituição não informada'} · Emenda: {plano.emendaCodigo || 'Não informada'}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <p className={`font-semibold ${strongTextClass}`}>
                      {pc.tipo === 'UNICA' ? 'PC única' : `PC ${pc.numero} de ${totalPcsDoPlano}`}
                    </p>
                    {pc.ehFinal && pc.tipo !== 'UNICA' && <p className={`text-xs ${accentTextClass}`}>final</p>}
                    <p className={`text-xs ${labelTextClass}`}>{pc.protocolo}</p>
                  </td>
                  <td className="px-4 py-4 align-top">
                    <SituacaoPcBadge situacao={pc.situacao} />
                    {pc.correcoesSolicitadas > 0 && (
                      <p className={`mt-1 text-[11px] ${labelTextClass}`}>
                        Correções: {pc.correcoesSolicitadas} de {pc.limiteCorrecoes}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-4 align-top">{renderPrazo(pc)}</td>
                  <td className={`px-4 py-4 align-top ${bodyTextClass}`}>
                    <p className={`font-semibold ${strongTextClass}`}>{pc.comprovantes.length}</p>
                    <p className={`text-xs ${labelTextClass}`}>
                      {ultimaMovimentacao ? `Últ. mov.: ${formatarData(ultimaMovimentacao)}` : 'Sem movimentação'}
                    </p>
                  </td>
                  <td className={`px-4 py-4 align-top font-medium ${bodyTextClass}`}>{currencyFmt.format(totalExecutado)}</td>
                  <td className="px-5 py-4 align-top">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/plano/prestacao-contas?planoId=${plano.id}`)}
                        className={`rounded-xl px-3 py-2 text-xs font-semibold transition ${primaryButtonClass}`}
                      >
                        Ver prestação
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/plano/full/${plano.id}`)}
                        className={`rounded-xl px-3 py-2 text-xs font-medium transition ${secondaryButtonClass}`}
                      >
                        Abrir plano
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DashboardPagination
        page={page}
        pageSize={pageSize}
        totalItems={filteredRows.length}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        itemLabel="PCs"
      />
    </div>
  );
};

export default PrestacoesPlanosGestaoPage;
